import React, { useMemo, useContext } from 'react';
import { Skeleton } from '@material-ui/lab';
import { cloneDeep } from 'lodash';
import {
  MODAL_KEY, DROPDOWN, QUANTITY_RANGE_RESPONSE,
  CONFIRMATION_POPUP_KEY, RESPONSE_POPUP_KEY,
  PRODUCT_TO_BE_CHANGED, PRODUCT_INFO,
} from '../../../constants/patientChart.constant';
import { PatientChartContext } from '../../../contexts/patientChart';
import PPButton from '../../subComponents/button';
import Select from '../../subComponents/select';
import { POPUP_TYPE } from '../../subComponents/popup';

const SkeletonLoader = () => <Skeleton variant='text' className='flex width-140' />;

export const ChangeProduct = ({
  orderId = 0,
  doctorId = 0,
  product = PRODUCT_INFO(),
  changeProducts = {},
  quantityRangeForProduct = QUANTITY_RANGE_RESPONSE(),
  changeProductForOrderAPI = {},

  getQuantity = async (requestBody = { OrderId: '', ProductId: '' }) => ({}),
  changeProduct = async (requestBody = {}) => ({}),
  getNotesTabInfo = async () => {},
}) => {
  const { handleModalOpen, handleMultipleModalClose, productToBeChanged, setProductToBeChanged, handleLoadProductsListing } = useContext(PatientChartContext);

  const { products, changeProductsCollection } = useMemo(() => {
    let _products = [DROPDOWN()];
    _products = [];
    const changeProductsCollection = new Map([
      [0, DROPDOWN()],
    ]);
    changeProductsCollection.delete(0);

    const productsCollection = new Map(changeProducts.api.data);
    if (!productsCollection.has(productToBeChanged.product.ProductId)) {
      return {
        products: _products,
        changeProductsCollection,
      };
    }

    const _p = productsCollection.get(productToBeChanged.product.ProductId);
    if (!_p?.length) {
      return {
        products: _products,
        changeProductsCollection,
      };
    }

    _products = _p.map(({
      Value: value,
      PrescriberProductDisplayName,
      Title,
    }) => {
      const product = { id: value, value, label: PrescriberProductDisplayName || Title, description: Title };
      changeProductsCollection.set(product.id, product);
      return product;
    });

    return {
      products: _products,
      changeProductsCollection,
    };
  }, [productToBeChanged.product.ProductId, changeProducts.api.data]);

  const selectedProduct = useMemo(() => {
    if (changeProductsCollection.has(productToBeChanged.changedProduct)) {
      return changeProductsCollection.get(productToBeChanged.changedProduct);
    }
    return DROPDOWN();
  }, [changeProductsCollection, productToBeChanged.changedProduct]);

  const quantities = useMemo(() => quantityRangeForProduct.api.data.quantities, [quantityRangeForProduct.api.data.quantities]);

  const loading = useMemo(() => changeProductForOrderAPI.loading, [changeProductForOrderAPI.loading]);

  const disabled = useMemo(() => {
    if (quantityRangeForProduct.api.loading) {
      return true;
    }

    if (loading) {
      return true;
    }

    if (productToBeChanged.changedProduct === -1) {
      return true;
    }

    if (productToBeChanged.quantity === -1) {
      return true;
    }

    if (
      productToBeChanged.changedProduct === productToBeChanged.product.ProductId &&
      productToBeChanged.quantity === productToBeChanged.product.Qty
    ) {
      return true;
    }

    if (productToBeChanged.reasonForChange.length < 3) {
      return true;
    }

    return false;
  }, [loading, productToBeChanged]);

  const handleChange = async ({ name }, { target }) => {
    const { value } = target;
    const pC = cloneDeep(productToBeChanged);

    // sets the change
    pC[name] = value;

    if (name === 'changedProduct') {
      pC.quantity = -1;
    }

    setProductToBeChanged({ ...pC });

    try {
      if (name === 'changedProduct' && value !== -1) {
        const { data, status } = await getQuantity({ OrderId: orderId, ProductId: value });
        if (status === 200) {
          const _q = data?.Data?.QTY || '0';
          let items = [0];
          items = _q.split(',') || [];
          const quantities = items.map((item = '') => Number(item));

          if (quantities.length) {
            pC.quantity = quantities[0];
          }

          if (data?.Data?.DefaultQty) {
            pC.quantity = Number(data.Data.DefaultQty);
          }

          setProductToBeChanged({ ...pC });
        }
      }
    } catch (e) {
      // 
    }
  };

  const handleReasonChange = ({ target }) => {
    const { name, value } = target;
    setProductToBeChanged({ ...productToBeChanged, [name]: value });
  };

  const handleChangeProduct = async ({ keys = [''] }) => {
    handleMultipleModalClose({ keys: [CONFIRMATION_POPUP_KEY.CHANGE_PRODUCT] });

    try {
      const requestBody = {
        OrderId: orderId,
        DoctorId: doctorId,
        OldProductId: productToBeChanged.product.ProductId,
        NewProductId: productToBeChanged.changedProduct,
        Qty: productToBeChanged.quantity,
        ReasonForChange: productToBeChanged.reasonForChange,
        storefrontCompanyId: product.StoreFrontData.CompanyId,
      };
  
      const { status, data } = await changeProduct(requestBody);
      if (status === 200) {
        handleMultipleModalClose.call(this, { keys });
        setProductToBeChanged(PRODUCT_TO_BE_CHANGED());
        handleLoadProductsListing({ OrderId: orderId });

        // reload the notes
        getNotesTabInfo();
      }

      if (status !== 200) {
        const { Message: message = 'Failed to change product. Please try later.' } = data;
        const heading = 'Error';

        handleModalOpen({
          key:
          RESPONSE_POPUP_KEY.CHANGE_PRODUCT, modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message,
          },
        });
      }
    } catch (e) {
      // 
    }
  };

  const QuantityCollection = new Map([
    [true, <SkeletonLoader />],
    [false, (
      <div className='flex max-width-140'>
        <Select
          name='quantity'
          labelId='quantity'
          value={productToBeChanged.quantity}
          onChange={handleChange.bind(this, { name: 'quantity' })}
          items={quantities}
        />
      </div>
    )],
  ]);

  const Template = (
    <div className='flex flex-column gap-20 flex-fill'>
      <div className='flex flex-column gap-8 justify-content-center'>
        <label className='input-label font-roboto'>Product:</label>
        <Select
          name='changedProduct'
          labelId='changedProduct'
          value={productToBeChanged.changedProduct}
          onChange={handleChange.bind(this, { name: 'changedProduct' })}
          items={products}
        />
      </div>

      <div className='flex flex-column gap-8 justify-content-center'>
        <label className='input-label font-roboto'>Quantity:</label>
        {QuantityCollection.get(quantityRangeForProduct.api.loading)}
      </div>

      <div className='flex flex-column flex-fill gap-8 justify-content-center'>
        <label className="input-label font-roboto">Reason for Change:</label>
        <textarea
          name='reasonForChange'
          className='flex flex-fill border-light-grey-1 min-height-160 height-full p-3 font-proxima-nova font-14 color-dark-grey border-box resize-none'
          wrap='soft'
          maxLength={4000}
          value={productToBeChanged.reasonForChange}
          onChange={handleReasonChange}
        />
      </div>
    </div>
  );

  const Actions = (
    <div className='flex justify-content-flex-end'>
      <PPButton
        label='CHANGE PRODUCT'
        variant='contained'
        loading={loading}
        disabled={disabled}
        endGutter
        onClick={handleModalOpen.bind(this, {
          key: CONFIRMATION_POPUP_KEY.CHANGE_PRODUCT,
          modal: {
            has: true,
            heading: 'Confirmation',
            message: (
              <div className='flex flex-wrap gap-8'>
                <div className='flex'>Confirm you are changing the product from</div>
                <div className='flex bold'>{productToBeChanged.product.displayTitle}</div>
                <div className='flex'>to</div>
                <div className='flex bold'>{selectedProduct.label}</div>
                <div className='flex'>?</div>
              </div>
            ),
            type: POPUP_TYPE.INFO,
            onConfirm: handleChangeProduct.bind(this, { keys: [MODAL_KEY.CHANGE_PRODUCT, CONFIRMATION_POPUP_KEY.CHANGE_PRODUCT] }),
            onCancel: handleMultipleModalClose.bind(this, { keys: [CONFIRMATION_POPUP_KEY.CHANGE_PRODUCT] }),
          },
        })}
      />
      <PPButton label='CANCEL' variant='cancel' onClick={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.CHANGE_PRODUCT] })} />
    </div>
  );

  return (
    <div className='flex flex-column gap-20 flex-fill m-5 patient-chart-container'>
      {Template}
      {Actions}
    </div>
  );
};

export default ChangeProduct;
